import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Box, TextField, Typography, Tooltip, Badge,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  makeStyles, CircularProgress
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DoneIcon from "@material-ui/icons/Done";
import DeleteIcon from "@material-ui/icons/Delete";
import NotificationsIcon from "@material-ui/icons/Notifications";
import config from "../../Helpers/config.json";
import { formatAmount } from "../../Utils/formatNumbers";
import { dateStringTimeConvert } from "../../Utils/titleCaseFunction";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";

const BRAND_COLOR = "#107c8e";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(4), backgroundColor: "#f5fafa", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing(3) },
  title: { fontWeight: 700, color: BRAND_COLOR, fontSize: "1.9rem" },
  bellBadge: { "& .MuiBadge-badge": { backgroundColor: "#e74c3c", color: "white", fontWeight: "bold" } },
  searchBox: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": { borderRadius: 12, backgroundColor: "white", boxShadow: "inset 0 2px 8px rgba(0,0,0,0.08)" }
  },
  tableContainer: { borderRadius: 16, overflow: "hidden", boxShadow: "0 10px 30px rgba(16,124,142,0.12)", border: "1px solid #a0d9e4" },
  tableHead: {
    backgroundColor: "#e8f4f8",
    "& th": { fontWeight: 700, color: BRAND_COLOR, textTransform: "uppercase", fontSize: "0.85rem", letterSpacing: "0.8px", borderBottom: `3px solid ${BRAND_COLOR}` }
  },
  tableRow: { transition: "all 0.3s ease", "&:hover": { backgroundColor: "#d0edf4 !important", transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(16,124,142,0.15)" } },
  chip: { fontWeight: 600, borderRadius: 20, padding: "4px 10px" },
  chipCash: { backgroundColor: "#43a047", color: "white" },
  chipCredit: { backgroundColor: "#1e88e5", color: "white" },
  chipRetail: { backgroundColor: "#fb8c00", color: "white" },
  chipWholesale: { backgroundColor: "#8e24aa", color: "white" },
  amountCell: { fontWeight: 700, color: BRAND_COLOR, fontSize: "1.1rem" },
  actionBtn: { minWidth: 40, padding: "8px 12px", borderRadius: 10, fontWeight: 600, "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(0,0,0,0.2)" } },
  btnApprove: { backgroundColor: BRAND_COLOR, color: "white", "&:hover": { backgroundColor: "#0e95a8" } },
  btnDelete: { backgroundColor: "#e74c3c", color: "white", "&:hover": { backgroundColor: "#c0392b" } },
  btnView: { backgroundColor: "#0984e3", color: "white", "&:hover": { backgroundColor: "#0773c4" } },
  btnBigInvoice: { backgroundColor: "#636e72", color: "white", fontSize: "0.8rem", "&:hover": { backgroundColor: "#4d565a" } },
  emptyState: { textAlign: "center", padding: "80px 20px", color: "#636e72", fontSize: "1.3rem", fontStyle: "italic" },
}));

const PendingDispensarRequests = ({ actions, history }) => {
  const classes = useStyles();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState("idle");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedToApprove, setSelectedToApprove] = useState(null);

  const fetchRequests = useCallback(() => {
    setLoading("loading");
    axios.get(`${config.epharmUrl}/Cinvoice/cashier/pending-invoices`)
      .then(res => {
        const data = res.data?.data || {};
        setRequests(data.invoices || []);
        setTotalRecords(data.total_records || 0);
        setLoading("success");
      })
      .catch(() => {
        actions.snackbarActions.errorSnackbar("Failed to load pending requests.");
        setLoading("error");
      });
  }, [actions]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApproveClick = (invoice) => {
    setSelectedToApprove(invoice);
    setApproveDialogOpen(true);
  };

  const confirmApprove = () => {
    if (!selectedToApprove) return;
    handleOpenDraft(selectedToApprove);
    setApproveDialogOpen(false);
    setSelectedToApprove(null);
  };

  const handleOpenDraft = async (invoice) => {
    const { invoice_number, customer_id, sale_type, payment_type } = invoice;
    const formData = new FormData();
    formData.append("receipt_number", "");
    formData.append("invoice_number", invoice_number);
    formData.append("customer_id", customer_id);

    try {
      const response = await axios.post(`${config.epharmUrl}/Cinvoice/view_receipt`, formData);
      const dataRes = response.data || {};
      const arr = dataRes.bill || [];
      const customer_details = dataRes.customer_details || {};
      const obj = arr[0] || {};

      sessionStorage.setItem(invoice_number, JSON.stringify({
        products: arr,
        customer: { ...customer_details, ...obj },
        loading: "success",
      }));

      const base = payment_type === 1 ? "/cashier" : "/cashierinvoice";
      const type = sale_type === "retail" ? "retail" : "wholesale";
      history.push(`${base}/${invoice_number}/${type}`);
    } catch (error) {
      actions.snackbarActions.errorSnackbar("Failed to open invoice.");
    }
  };

  // FULLY WORKING DELETE
  const handleDeleteConfirm = async () => {
    if (!selectedInvoice || deleting) return;

    setDeleting(true);
    try {
      await axios.post(`${config.epharmUrl}/Cinvoice/cashier/delete-invoice`, {
        invoice_id: selectedInvoice.invoice_id,
      });

      actions.snackbarActions.successSnackbar(`Invoice ${selectedInvoice.invoice_number} deleted successfully.`);

      // Remove instantly from UI
      setRequests(prev => prev.filter(r => r.invoice_id !== selectedInvoice.invoice_id));
      setTotalRecords(prev => prev - 1);

    } catch (err) {
      actions.snackbarActions.errorSnackbar("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
    }
  };

  const filteredRequests = requests.filter(req =>
    [req.invoice_number, req.customer_name, req.dispenser_name]
      .join(" ").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.title}>Pending Dispensar Requests</Typography>
        <Badge badgeContent={totalRecords} color="secondary" className={classes.bellBadge}>
          <NotificationsIcon style={{ fontSize: 34, color: BRAND_COLOR }} />
        </Badge>
      </Box>

      <TextField
        fullWidth variant="outlined" placeholder="Search by invoice, customer, or dispenser..."
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        className={classes.searchBox}
      />

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCell>Sl</TableCell>
              <TableCell>Invoice#</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Sale Type</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Dispenser</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading === "loading" && (
              <TableRow><TableCell colSpan={9} align="center" style={{ padding: 60 }}>Loading...</TableCell></TableRow>
            )}
            {loading === "success" && filteredRequests.length === 0 && (
              <TableRow><TableCell colSpan={9} className={classes.emptyState}>
                {searchTerm ? "No matching requests found." : "No pending requests at the moment."}
              </TableCell></TableRow>
            )}
            {loading === "success" && filteredRequests.map((req, i) => (
              <TableRow key={req.invoice_id} hover className={classes.tableRow}>
                <TableCell>{i + 1}</TableCell>
                <TableCell><strong>{req.invoice_number}</strong></TableCell>
                <TableCell>{req.customer_name || "-"}</TableCell>
                <TableCell>
                  <Chip label={req.sale_type === "wholesale" ? "Facility Sale" : "Client Sale"}
                    className={`${classes.chip} ${req.sale_type === "wholesale" ? classes.chipWholesale : classes.chipRetail}`} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label={req.payment_type === 1 ? "Cash" : "Credit"}
                    className={`${classes.chip} ${req.payment_type === 1 ? classes.chipCash : classes.chipCredit}`} size="small" />
                </TableCell>
                <TableCell className={classes.amountCell}>{formatAmount(req.total_amount)}</TableCell>
                <TableCell>{dateStringTimeConvert(req.date)}</TableCell>
                <TableCell>{req.dispenser_name || "-"}</TableCell>
                <TableCell>
                  <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                    <Tooltip title="Approve Sale">
                      <Button variant="contained" size="small" className={`${classes.action587Btn} ${classes.btnApprove}`}
                        onClick={() => handleApproveClick(req)}>
                        <DoneIcon fontSize="small" />
                      </Button>
                    </Tooltip>

                    <Tooltip title="Delete Request">
                      <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnDelete}`}
                        onClick={() => { setSelectedInvoice(req); setDeleteDialogOpen(true); }} disabled={deleting}>
                        {deleting && selectedInvoice?.invoice_id === req.invoice_id ? 
                          <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                      </Button>
                    </Tooltip>

                    <Tooltip title="View Invoice">
                      <Link to={`/invoice_print_details/${req.customer_id}/${req.invoice_number}`} style={{ textDecoration: "none" }}>
                        <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnView}`}>
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </Link>
                    </Tooltip>

                    <Tooltip title="View Big Invoice">
                      <Link to={`/invoice_print_big/${req.customer_id}/${req.invoice_number}/${false}/${true}`}>
                        <Button variant="contained" size="small" className={`${classes.actionBtn} ${classes.btnBigInvoice}`}>Big</Button>
                      </Link>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* FULLY WORKING DELETE DIALOG - NO BROKEN PROPS */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle style={{ backgroundColor: "#e74c3c", color: "white" }}>
          Delete Pending Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginTop: 16, fontSize: "1rem" }}>
            Are you sure you want to delete invoice <strong>{selectedInvoice?.invoice_number}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} style={{ backgroundColor: "#e74c3c", color: "white" }} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle style={{ backgroundColor: BRAND_COLOR, color: "white" }}>Approve Sale</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginTop: 16 }}>
            Approve sale for invoice <strong>{selectedToApprove?.invoice_number}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmApprove} style={{ backgroundColor: BRAND_COLOR, color: "white" }}>Approve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(null, mapDispatchToProps)(PendingDispensarRequests);